/* eslint-disable react/prop-types */
/**
 * AssignmentRecordingsTab — county-clerk filing record for assignment deals.
 *
 * Only meaningful when the parent deal's `deal_type === "ASSIGNMENT"`, but
 * the constraint is app-layer only — the backend will still accept rows
 * for other deal types, so we show a notice (not a hard block) and let
 * the user proceed.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Card, Space } from "antd";

import { WigMetadataCrudPanel } from "@warwick-investment-group/wig-react-library";

import { AppContext } from "../../../AppContextProvider";
import usePermissions from "../../../hooks/usePermissions";
import { countyFieldRenderer } from "../../../components/countySelect";
import { stripReadOnlyForUpdate } from "../../../utils/entityUpdateBody";
import dealsService from "../../../services/dealsService";
import { apiErrorText } from "../../../utils/apiError";
import { isRecordingsApplicable } from "../../../utils/recordingsApplicable";

// Strip from metadata before passing — the library panel has no `hideFields`
// prop, so pre-filtering is the supported way to suppress grid + form rows.
const HIDDEN_FIELDS = new Set(["deal_id"]);

const AssignmentRecordingsTab = ({ dealId, deal }) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  // POST/PUT/DELETE /assignment_recordings are [BD.Landman, BD.LandTech];
  // `recording.record` mirrors that list exactly.
  const { can } = usePermissions();
  const mayRecord = can("recording.record");

  const [rows, setRows] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState(null);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const refresh = useCallback(async () => {
    if (!dealId) return;
    try {
      setLoading(true);
      setErrMsg(null);
      const { token: tk, userOid } = await getAuth();
      const [listResp, metaResp] = await Promise.all([
        dealsService.listAssignmentRecordings({ token: tk, userOid, dealId }),
        dealsService.getMetadata({ token: tk, userOid, tableName: "assignment_recordings" }),
      ]);
      setRows(listResp?.data?.data || []);
      setMetadata(metaResp?.data?.data || []);
    } catch (err) {
      console.error("AssignmentRecordingsTab load failed:", err);
      setErrMsg(apiErrorText(err));
    } finally {
      setLoading(false);
    }
  }, [dealId, getAuth]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredMetadata = useMemo(
    () => (metadata || []).filter((m) => !HIDDEN_FIELDS.has(m.field)),
    [metadata]
  );

  const onCreate = async (body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.createAssignmentRecording({
      token: tk,
      userOid,
      body: { ...body, deal_id: dealId },
    });
    await refresh();
    return resp;
  };

  const onUpdate = async (id, body) => {
    const { token: tk, userOid } = await getAuth();
    const resp = await dealsService.updateAssignmentRecording({
      token: tk,
      userOid,
      id,
      body: stripReadOnlyForUpdate(body),
    });
    await refresh();
    return resp;
  };

  const onDelete = async (id) => {
    const { token: tk, userOid } = await getAuth();
    await dealsService.deleteAssignmentRecording({ token: tk, userOid, id });
    await refresh();
  };

  const dealType = String(deal?.deal_type || "").toUpperCase();
  // Same predicate the deal page uses to decide whether to offer this tab at
  // all, so a divestiture (recordable, but deal_type != 'ASSIGNMENT') isn't
  // warned about a filing it legitimately has.
  const showNonAssignmentNotice = !!deal && !isRecordingsApplicable(deal);

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {errMsg && (
        <Alert
          type="error"
          showIcon
          message="Failed to load assignment recordings"
          description={errMsg}
        />
      )}
      {showNonAssignmentNotice && (
        <Alert
          type="warning"
          showIcon
          message="Recording requires deal.deal_type='ASSIGNMENT'"
          description={`This deal's type is '${dealType}'. You can still add rows here, but recordings normally only apply to assignment deals.`}
        />
      )}
      <Card size="small" title={`Assignment recordings (${rows.length})`}>
        <WigMetadataCrudPanel
          title="County clerk filings"
          rows={rows}
          metadata={filteredMetadata}
          loading={loading}
          allowView
          disableNonEditable
          onCreate={mayRecord ? onCreate : undefined}
          onUpdate={mayRecord ? onUpdate : undefined}
          onDelete={mayRecord ? onDelete : undefined}
          customRenderers={{ county: countyFieldRenderer }}
          newRowDefaults={{ deal_id: dealId }}
          emptyText="No assignment recordings yet"
          editTrigger="button"
          editLayout="wide"
          height={400}
        />
      </Card>
    </Space>
  );
};

export default AssignmentRecordingsTab;
